import {Icon, Tooltip, Typography} from "antd";
import moment from "moment";
import React from "react";

export type SafetyCardCredential = {
    type?: string,
    expiration?: string
}

export interface SafetyCardStatusProps {
    credentials?: SafetyCardCredential[] | null
}

export type SafetyCardAssessment = {
    status: 'missing' | 'expired' | 'unknown' | 'valid',
    expirationDate?: string
}

function getLatestSafetyCard(credentials?: SafetyCardCredential[] | null): SafetyCardCredential | undefined {
    return (credentials || [])
        .filter((credential) => credential?.type === 'SAFETY_CARD')
        .reduce((latest: SafetyCardCredential | undefined, credential) => {
            if (!latest) return credential;

            const latestExpiration = moment(latest.expiration, 'YYYY-MM-DD', true);
            const credentialExpiration = moment(credential.expiration, 'YYYY-MM-DD', true);

            if (!credentialExpiration.isValid()) return latest;
            if (!latestExpiration.isValid()) return credential;

            return credentialExpiration.isAfter(latestExpiration) ? credential : latest;
        }, undefined);
}

export function assessSafetyCard(credentials?: SafetyCardCredential[] | null): SafetyCardAssessment {
    const safetyCard = getLatestSafetyCard(credentials);

    if (!safetyCard) {
        return {status: 'missing'};
    }

    const expiration = moment(safetyCard.expiration, 'YYYY-MM-DD', true);
    if (!expiration.isValid()) {
        return {status: 'unknown'};
    }

    const expirationDate = expiration.format('YYYY-MM-DD');
    if (expiration.isSameOrBefore(moment(), 'day')) {
        return {status: 'expired', expirationDate};
    }

    return {status: 'valid', expirationDate};
}

const SafetyCardStatus: React.FC<SafetyCardStatusProps> = ({credentials}) => {
    const assessment = assessSafetyCard(credentials);

    if (assessment.status === 'missing') {
        return (
            <Tooltip title="No Safety Card is on file">
                <Typography.Text type="danger">
                    <Icon type="close-circle" /> Missing
                </Typography.Text>
            </Tooltip>
        )
    }

    if (assessment.status === 'unknown') {
        return (
            <Tooltip title="The Safety Card does not have a valid expiration date">
                <Typography.Text type="warning">
                    <Icon type="warning" /> Expiration unknown
                </Typography.Text>
            </Tooltip>
        )
    }

    if (assessment.status === 'expired') {
        return (
            <Tooltip title={`Safety Card expired on ${assessment.expirationDate}`}>
                <Typography.Text type="danger">
                    <Icon type="exclamation-circle" /> Expired {assessment.expirationDate}
                </Typography.Text>
            </Tooltip>
        )
    }

    return (
        <Tooltip title={`Safety Card expires on ${assessment.expirationDate}`}>
            <span style={{color: 'green'}}>
                <Icon type="check-circle" /> Valid {assessment.expirationDate}
            </span>
        </Tooltip>
    )
}

export default SafetyCardStatus;
